import { createContext, useCallback, useContext, useEffect, useId, useState } from 'react';

/**
 * Shared stacking state: `[stackOrder, elevate, remove]`.
 *
 * `stackOrder` is the ordered list of registered component ids (last = topmost).
 * `elevate` moves an id to the top; `remove` unregisters it.
 */
export type ZIndexContextWrapper = [string[], (id: string) => void, (id: string) => void];

type UseZIndex = [number, () => void];

/** Base z-index that every stacked component is offset from. */
export const BASE_Z_INDEX = 2;

/**
 * Context carrying the shared stacking order for a group of floating/dockable
 * components. Provide it once (via {@link useZIndexContext2}) around the panels
 * that should share a stacking context.
 */
export const ZIndexContext2 = createContext<ZIndexContextWrapper | null>(null);

/**
 * Creates the shared stacking state for a group of stacked components. Call once
 * at the provider level and pass the result to `ZIndexContext2.Provider`.
 *
 * @returns A {@link ZIndexContextWrapper} tuple to spread into the provider.
 */
export function useZIndexContext2(): ZIndexContextWrapper {
    const [stackOrder, setStackOrder] = useState<string[]>([]);

    const elevateComponent = useCallback((id: string) => {
        setStackOrder((currentOrder) => {
            const filteredOrder = currentOrder.filter((existing) => existing !== id);
            return [...filteredOrder, id];
        });
    }, []);

    const removeComponent = useCallback((id: string) => {
        setStackOrder((currentOrder) => currentOrder.filter((existing) => existing !== id));
    }, []);

    return [stackOrder, elevateComponent, removeComponent];
}

/**
 * Subscribes a single stacked component to the shared {@link ZIndexContext2}.
 * Must be called within a `ZIndexContext2.Provider`.
 *
 * @returns `[zIndex, elevate]` — the component's current z-index and a callback
 * that brings it to the front.
 * @throws If called outside a `ZIndexContext2.Provider`.
 */
export function useZIndex2(stackingId?: string): UseZIndex {
    const generatedId = useId();
    const id = stackingId ?? generatedId;
    const context = useContext(ZIndexContext2);
    if (!context) {
        throw new Error('useZIndex must be used within an ZIndexContext provider');
    }
    const [stackOrder, elevateComponent, removeComponent] = context;

    const elevate = useCallback(() => {
        elevateComponent(id);
    }, [id, elevateComponent]);

    useEffect(() => {
        return () => {
            removeComponent(id);
        };
    }, [id, removeComponent]);

    const position = stackOrder.indexOf(id);
    const zIndex = BASE_Z_INDEX + position;

    return [zIndex, elevate];
}

/** Applies a host-managed back-to-front order within the shared stacking context. */
export function useZIndexOrder(stackingIds: readonly string[]): void {
    const context = useContext(ZIndexContext2);
    if (!context) {
        throw new Error('useZIndexOrder must be used within an ZIndexContext provider');
    }
    const [, elevateComponent] = context;

    useEffect(() => {
        for (const id of stackingIds) {
            elevateComponent(id);
        }
    }, [elevateComponent, stackingIds]);
}
